import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Check, Loader2, MessageSquareQuote, Minus } from "lucide-react";

import { buildSeo } from "@/lib/seo";
import { MarketingLayout, Eyebrow, useHydratedTranslation } from "@/components/marketing/Layout";
import { getTopic, TOPICS } from "@/lib/topics";
import { TOPIC_LOCALES } from "@/lib/localized-topics.functions";
import { LANG_META } from "@/i18n";
import { useAutoTranslate } from "@/components/AutoTranslate";

const SITE = "https://realitytwin.io";

export const Route = createFileRoute("/topics/$slug")({
  loader: ({ params }) => {
    if (!getTopic(params.slug)) throw notFound();
    return null;
  },
  head: ({ params }) => {
    const topic = getTopic(params.slug);
    if (!topic)
      // Unknown slug: the loader throws notFound, but a crawler that reached
      // this URL still needs a sane title — and must not index it.
      return buildSeo({
        path: `/topics/${params.slug}`,
        title: "How agentic AI actually works in B2B | Reality Twin",
        description:
          "Explainers on agentic AI, machine-to-machine business and verified company identity — each one honest about what works today and what does not.",
        noindex: true,
      });
    const url = `${SITE}/topics/${topic.slug}`;
    const seo = buildSeo({
      path: `/topics/${topic.slug}`,
      title: topic.metaTitle,
      description: topic.metaDescription,
      type: "article",
    });
    return {
      ...seo,
      links: [
        ...seo.links,
        // The German page is a real, separately rendered URL — so unlike the
        // ?lang= alternates this used to carry, these are true.
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "de", href: `${SITE}/de/themen/${topic.slug}` },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            inLanguage: "en",
            headline: topic.metaTitle,
            description: topic.metaDescription,
            author: { "@type": "Organization", name: "Reality Twin" },
            publisher: { "@type": "Organization", name: "Reality Twin", url: SITE },
            mainEntityOfPage: url,
            about: { "@type": "Thing", name: topic.name },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: topic.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          // Breadcrumbs replace the bare URL in the result with a readable
          // path, which is worth more than it looks on a deep page.
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Reality Twin", item: SITE },
              { "@type": "ListItem", position: 2, name: "Topics", item: `${SITE}/topics` },
              { "@type": "ListItem", position: 3, name: topic.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: TopicPage,
});

function TopicPage() {
  const t = useHydratedTranslation();
  const { slug } = Route.useParams();
  const topic = getTopic(slug);

  // Authored in English and translated through the cached translator, like the
  // industry and competitor pages. The section headings go through it too —
  // leaving the chrome in English around translated prose reads as half-done.
  const CHROME = [
    "What this looks like in practice",
    "What is true today",
    "This section exists because most pages in this category do not have one. Both columns are maintained against the product's own status list, not written by marketing.",
    "Works today",
    "Does not, and we will not pretend",
    "See the full status of every feature",
    "Check it against your own website",
    "The free Twin-Check reads your site the way an AI assistant would and tells you what it can and cannot answer about you. No account needed.",
    "Questions people actually ask",
    "This page answers",
    "More on how this works",
  ];
  const src = topic
    ? [
        topic.name,
        topic.heroLead,
        ...topic.thesis,
        ...topic.inPractice.flatMap((s) => [s.title, s.body]),
        ...topic.reality.works,
        ...topic.reality.doesNot,
        ...topic.queries,
        ...topic.faqs.flatMap((f) => [f.q, f.a]),
        ...CHROME,
      ]
    : [];
  const { out, busy } = useAutoTranslate(src);
  if (!topic) return null;

  let n = 0;
  const name = out[n++];
  const heroLead = out[n++];
  const thesis = topic.thesis.map(() => out[n++]);
  const inPractice = topic.inPractice.map(() => ({ title: out[n++], body: out[n++] }));
  const works = topic.reality.works.map(() => out[n++]);
  const doesNot = topic.reality.doesNot.map(() => out[n++]);
  const queries = topic.queries.map(() => out[n++]);
  const faqs = topic.faqs.map(() => ({ q: out[n++], a: out[n++] }));
  const [
    cInPractice,
    cRealityTitle,
    cRealityLead,
    cWorks,
    cDoesNot,
    cToRoadmap,
    cCtaTitle,
    cCtaBody,
    cFaq,
    cAnswers,
    cMore,
  ] = CHROME.map(() => out[n++]);

  const others = TOPICS.filter((x) => x.slug !== topic.slug);

  return (
    <MarketingLayout>
      <article className="py-14 sm:py-20">
        {/* HERO */}
        <div className="mx-auto max-w-3xl px-6">
          <Eyebrow>
            <span className="inline-flex items-center gap-2">
              {name}
              {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            </span>
          </Eyebrow>
          <h1 className="mt-4 font-serif text-[36px] sm:text-[56px] leading-[1.03] tracking-[-0.03em]">
            {name}
          </h1>
          <p className="mt-6 text-[17px] leading-[1.6] text-muted-foreground">{heroLead}</p>
        </div>

        {/* THESIS */}
        <div className="mx-auto max-w-3xl px-6 mt-12 space-y-5">
          {thesis.map((p, i) => (
            <p key={i} className="text-[17px] leading-[1.7]">
              {p}
            </p>
          ))}
        </div>

        {/* IN PRACTICE */}
        <div className="mx-auto max-w-3xl px-6 mt-14">
          <h2 className="font-serif text-[26px] sm:text-[32px] tracking-[-0.02em]">
            {cInPractice}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {inPractice.map((s, i) => (
              <div key={i} className="rounded-2xl hairline bg-card p-5">
                <div className="font-medium">{s.title}</div>
                <p className="mt-2 text-[14px] leading-[1.6] text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* REALITY CHECK — the section that makes the page worth trusting. */}
        <div className="mx-auto max-w-3xl px-6 mt-14">
          <h2 className="font-serif text-[26px] sm:text-[32px] tracking-[-0.02em]">
            {cRealityTitle}
          </h2>
          <p className="mt-3 text-[15px] leading-[1.6] text-muted-foreground">{cRealityLead}</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl hairline bg-card p-5">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
                {cWorks}
              </div>
              <ul className="mt-3 space-y-2.5">
                {works.map((w, i) => (
                  <li key={i} className="flex gap-2 text-[14px] leading-[1.55]">
                    <Check
                      className="h-4 w-4 shrink-0 mt-0.5 text-[oklch(0.62_0.14_168)]"
                      strokeWidth={2}
                    />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl hairline bg-cream p-5">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
                {cDoesNot}
              </div>
              <ul className="mt-3 space-y-2.5">
                {doesNot.map((d, i) => (
                  <li
                    key={i}
                    className="flex gap-2 text-[14px] leading-[1.55] text-muted-foreground"
                  >
                    <Minus className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <Link
            to="/roadmap"
            className="mt-4 inline-flex items-center gap-1.5 text-[14px] hover:opacity-70"
          >
            {cToRoadmap}
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        </div>

        {/* CTA */}
        <div className="mx-auto max-w-3xl px-6 mt-14">
          <div className="rounded-2xl hairline bg-card p-6 sm:p-8">
            <h2 className="font-serif text-[24px] sm:text-[28px] tracking-[-0.02em]">
              {cCtaTitle}
            </h2>
            <p className="mt-3 text-[15px] leading-[1.6] text-muted-foreground">{cCtaBody}</p>
            <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-2">
              <Link
                to="/twin-check"
                className="inline-flex justify-center items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90"
              >
                {t("routes.industries.ctaCheck", {
                  defaultValue: "Check your AI-readability — free",
                })}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
              <Link
                to="/auth"
                className="inline-flex justify-center items-center rounded-full hairline bg-background px-5 py-3 text-[14px] font-medium hover:bg-cream transition"
              >
                {t("routes.industries.ctaStart", { defaultValue: "Build your Twin" })}
              </Link>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mx-auto max-w-3xl px-6 mt-14">
          <h2 className="font-serif text-[26px] sm:text-[32px] tracking-[-0.02em]">{cFaq}</h2>
          <div className="mt-6 divide-y divide-border hairline rounded-2xl bg-card">
            {faqs.map((f, i) => (
              <div key={i} className="p-5">
                <div className="font-medium">{f.q}</div>
                <p className="mt-2 text-[14px] leading-[1.6] text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* SEARCH INTENTS — helps the page rank for the phrasing people use. */}
        <div className="mx-auto max-w-3xl px-6 mt-12">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
            <MessageSquareQuote className="h-3.5 w-3.5" />
            {cAnswers}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {queries.map((q, i) => (
              <span key={i} className="rounded-full hairline bg-card px-3 py-1.5 text-[12.5px]">
                {q}
              </span>
            ))}
          </div>
        </div>

        {/* THIS PAGE IN OTHER LANGUAGES — hreflang tells search engines about
            these, but a crawler still needs a followable link, and without one
            the localized pages would only be reachable via the sitemap. */}
        <div className="mx-auto max-w-3xl px-6 mt-14">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
            {t("routes.topics.languages", { defaultValue: "This page in other languages" })}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-[13px] text-muted-foreground">
            <Link
              to="/de/themen/$slug"
              params={{ slug: topic.slug }}
              className="hover:text-foreground transition"
            >
              Deutsch
            </Link>
            {TOPIC_LOCALES.map((l) => (
              <Link
                key={l}
                to="/$lang/topics/$slug"
                params={{ lang: l, slug: topic.slug }}
                className="hover:text-foreground transition"
              >
                {LANG_META[l]?.native ?? l}
              </Link>
            ))}
          </div>
        </div>

        {/* OTHER TOPICS */}
        <div className="mx-auto max-w-3xl px-6 mt-14">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
            {cMore}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {others.map((o) => (
              <Link
                key={o.slug}
                to="/topics/$slug"
                params={{ slug: o.slug }}
                className="rounded-full hairline bg-card px-3.5 py-1.5 text-[13px] hover:bg-cream transition"
              >
                {o.name}
              </Link>
            ))}
          </div>
        </div>
      </article>
    </MarketingLayout>
  );
}
